// =============================================================================
// InputController.ts — Teclado (WASD + setas). O vetor é lido pelo loop de
// input (25Hz) e pela predição local (todo frame). Também roteia as teclas de
// urna (1/2/3) e o placar da CPI (segurar TAB).
// =============================================================================
import type { MoveVector } from '../infrastructure/NetClient';

export interface InputHandlers {
  onUrnaKey(idx: number): void;    // só chega se a urna estiver visível
  urnaVisible(): boolean;
  onStatsShow(): void;
  onStatsHide(): void;
  onPauseKey(): void;              // Esc: pausa/retoma (o servidor decide se pode)
  /** Código secreto digitado (dígitos, sem urna/campos de texto). */
  onDigitCode?(code: string): void;
}

export class InputController {
  private readonly keys = new Set<string>();
  /** Buffer dos últimos dígitos (easter egg 13-13-13…). */
  private digitBuf = '';

  constructor(handlers: InputHandlers) {
    addEventListener('keydown', (e) => {
      const key = e.key.toLowerCase();
      this.keys.add(key);
      // Setas e espaço rolariam a página no browser; aqui são teclas de jogo
      // (fora de campos de texto, onde as setas movem o cursor)
      const typing = e.target instanceof HTMLElement && ['INPUT', 'TEXTAREA'].includes(e.target.tagName);
      if (!typing && ['arrowup', 'arrowdown', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
      }
      // Urna: vota com 1/2/3, como manda o figurino
      if (handlers.urnaVisible() && ['1', '2', '3'].includes(e.key)) {
        handlers.onUrnaKey(Number(e.key) - 1);
      }
      // Easter egg: só em jogo (fora da urna e de inputs) — dígitos formam o código
      if (!typing && !handlers.urnaVisible() && !e.repeat && /^\d$/.test(e.key)) {
        this.digitBuf = (this.digitBuf + e.key).slice(-16);
        if (this.digitBuf.endsWith('1313131313')) {
          this.digitBuf = '';
          handlers.onDigitCode?.('1313131313');
        }
      } else if (!typing && !/^\d$/.test(e.key) && e.key.length === 1) {
        // Tecla "normal" quebra a sequência (setas/modificadores não)
        this.digitBuf = '';
      }
      // Esc: pausa/retoma no modo solo (com urna aberta o mundo já está parado)
      if (key === 'escape' && !e.repeat && !typing && !handlers.urnaVisible()) {
        handlers.onPauseKey();
      }
      // TAB segurando: placar da CPI (skills, níveis e inimigos cassados).
      // preventDefault sempre: senão o foco pula de elemento e o browser rola a
      // tela atrás dele. O guard de e.repeat evita reconstruir o placar a cada
      // repetição da tecla (o que zerava o scroll interno da caixa).
      if (e.key === 'Tab') {
        e.preventDefault();
        if (!e.repeat) handlers.onStatsShow();
      }
    });
    addEventListener('keyup', (e) => {
      this.keys.delete(e.key.toLowerCase());
      if (e.key === 'Tab') handlers.onStatsHide();
    });
    addEventListener('blur', () => {
      this.keys.clear();
      handlers.onStatsHide();
    });
  }

  vector(): MoveVector {
    let mx = 0, my = 0;
    if (this.keys.has('a') || this.keys.has('arrowleft')) mx -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) mx += 1;
    if (this.keys.has('w') || this.keys.has('arrowup')) my -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) my += 1;
    const mag = Math.hypot(mx, my);
    return mag > 1 ? { mx: mx / mag, my: my / mag } : { mx, my };
  }
}
